import { UseFormReturn } from 'react-hook-form';
import isEqual from 'react-fast-compare';
import { subscribeFormValueEvents } from '@srm/ui/lib/rhfBridge';
import { IField, IFieldElem } from '@models/IFormData';
import { ITableForm } from '@models/Forms/ITableForm';

/** Parent is an alias, not another saved field. Each nested level supplies its own map. */
export const withRowParentFields = (
    fields: Record<string, IFieldElem>,
    rowFields: Record<string, IFieldElem>,
): Record<string, IFieldElem> => ({
    ...fields,
    ...Object.fromEntries(Object.entries(rowFields).map(([key, field]) => [`|Parent${key}`, field])),
});

/**
 * A row form has flat controls, while document formulas and changes address the
 * original table. Keep both representations in the SAME local RHF draft. The
 * guarded synchronous subscription also covers writes made by input controllers.
 */
export const connectRowFormDraft = (
    methods: UseFormReturn<ITableForm>,
    tablePath: string,
    rowKey: unknown,
    rowFields: Record<string, IFieldElem>,
) => {
    let syncing = false;
    const normalize = (path: string) => path.replace(/\.?\[(\d+)\]/g, '.$1');
    const normalizedTablePath = normalize(tablePath);
    const rowEntries = Object.entries(rowFields);
    // события без подписки на значения: watch(callback) клонировал бы весь черновик с таблицей на каждый символ
    return subscribeFormValueEvents(methods.control, ({ name }) => {
        if (syncing || !name) return;
        syncing = true;
        try {
            const rows = methods.getValues(tablePath as never) as unknown as Record<string, unknown>[];
            if (!Array.isArray(rows)) return;
            const index = rows.findIndex((row) => row['|NUM'] === rowKey);
            if (index < 0) return;
            const changedPath = normalize(name);
            if (changedPath === normalizedTablePath || changedPath.startsWith(`${normalizedTablePath}.`)) {
                for (const [key, field] of rowEntries) {
                    const value = rows[index][key];
                    if (!isEqual(methods.getValues(field.name as never), value)) {
                        methods.setValue(field.name as never, value as never, { shouldDirty: true });
                    }
                }
            } else {
                const changed = rowEntries.find(([, field]) => {
                    const path = normalize(field.name);
                    return changedPath === path || changedPath.startsWith(`${path}.`);
                });
                if (!changed) return;
                const [key, field] = changed;
                const value = methods.getValues(field.name as never) as unknown as IField['value'];
                if (isEqual(rows[index][key], value)) return;
                methods.setValue(`${tablePath}.${index}` as never, { ...rows[index], [key]: value } as never, {
                    shouldDirty: true,
                });
            }
        } finally {
            syncing = false;
        }
    });
};
